import { apiPost } from '@/api/http'

export interface LogEventParams {
  eventType: string
  targetType?: string | null
  targetId?: number | null
  metaInt1?: number | null
  metaStr1?: string | null
  metaStr2?: string | null
  sessionId?: string | null
}

// Queue pour accumuler les logs
const logQueue: LogEventParams[] = []
let batchTimer: ReturnType<typeof setTimeout> | null = null
const BATCH_INTERVAL = 30000 // 30 secondes
const MAX_BATCH_SIZE = 50 // Envoyer par batch même si < 30s si on atteint 50 logs

/**
 * Envoyer les logs accumulés par batch
 */
async function flushLogs(): Promise<void> {
  if (logQueue.length === 0) {
    return
  }

  const logsToSend = [...logQueue]
  logQueue.length = 0 // Vider la queue

  try {
    // Envoyer tous les logs en une seule requête
    await apiPost<{ data: unknown }>('/stats/log/batch', {
      events: logsToSend,
    })
    //console.log(`[BdC-UsageEventService] ${logsToSend.length} événements envoyés par batch`)
  } catch (error) {
    // Logging silencieux : on ne bloque pas l'UX si le logging échoue
    console.warn('[BdC-UsageEventService] Erreur envoi batch logs (silencieux):', error)
    // Remettre les logs dans la queue pour réessayer plus tard (limiter à 100 pour éviter débordement)
    if (logQueue.length < 100) {
      logQueue.unshift(...logsToSend)
    }
  }
}

/**
 * Programmer l'envoi du batch
 */
function scheduleBatch(): void {
  if (batchTimer) {
    return // Un batch est déjà programmé
  }

  batchTimer = setTimeout(() => {
    batchTimer = null
    flushLogs().catch(err => {
      console.warn('[BdC-UsageEventService] Erreur flush batch:', err)
    })
  }, BATCH_INTERVAL)
}

/**
 * Service pour logger les événements d'usage avec batching
 * Gestion silencieuse des erreurs pour ne pas bloquer l'UX
 */
export const usageEventService = {
  /**
   * Log un événement d'usage (ajouté à la queue, envoyé par batch toutes les 5s)
   * Les erreurs sont silencieuses (ne bloquent pas l'UX)
   */
  async logEvent(params: LogEventParams): Promise<void> {
    try {
      // Générer un sessionId si non fourni (approximation basée sur timestamp + user)
      const sessionId =
        params.sessionId || `session_${Date.now()}_${Math.random().toString(36).substring(7)}`

      // Ajouter à la queue
      logQueue.push({
        ...params,
        sessionId,
      })

      // Si on atteint la taille max, envoyer immédiatement
      if (logQueue.length >= MAX_BATCH_SIZE) {
        if (batchTimer) {
          clearTimeout(batchTimer)
          batchTimer = null
        }
        await flushLogs()
      } else {
        // Sinon, programmer l'envoi du batch
        scheduleBatch()
      }
    } catch (error) {
      // Logging silencieux : on ne bloque pas l'UX si le logging échoue
      console.warn('[BdC-UsageEventService] Erreur ajout événement à la queue (silencieux):', error)
    }
  },

  /**
   * Forcer l'envoi immédiat des logs en attente (utile avant déconnexion)
   */
  async flush(): Promise<void> {
    if (batchTimer) {
      clearTimeout(batchTimer)
      batchTimer = null
    }
    await flushLogs()
  },
}

// Envoyer les logs restants avant la fermeture de la page
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    if (logQueue.length > 0) {
      // Utiliser sendBeacon pour garantir l'envoi même si la page se ferme
      const logsToSend = JSON.stringify({ events: logQueue })
      navigator.sendBeacon(
        `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api/v1'}/stats/log/batch`,
        logsToSend
      )
    }
  })
}
